/**
 * `/api/users`, `/api/groups`, `/api/roles`, `/api/permissions`, `/api/subjects`.
 *
 * One file because they are one subject — people, what they belong to, and what
 * they may do — and every screen that touches one touches another.
 *
 * Note what is missing: there is no delete for a user. That is the backend's
 * decision, not an oversight here; audit rows must keep naming people. Users
 * are deactivated instead.
 *
 * Five calls can take permissions away from the caller and take a `Lockout`
 * argument: without `acceptLockout` such a change answers 409 `self_lockout`.
 * Wrap them in `useSelfLockout().guard`, which asks and repeats the call.
 */
import type {
  AddGroupMemberRequest,
  AssignRoleRequest,
  CreateGroupRequest,
  CreateRoleRequest,
  CreateUserRequest,
  DeleteGroupResponse,
  DeleteRoleResponse,
  GroupListResponse,
  GroupMemberListResponse,
  GroupResponse,
  GroupUsageResponse,
  ListQueryParams,
  MembershipsResponse,
  OkResponse,
  PermissionListResponse,
  RoleAssigneeListResponse,
  RoleListResponse,
  RoleResponse,
  RoleUsageResponse,
  SelfLockoutQuery,
  SetPasswordChangeRequiredRequest,
  SetPasswordRequest,
  SubjectSearchResponse,
  SubjectType,
  UpdateGroupRequest,
  UpdateRoleRequest,
  UpdateRolePermissionsRequest,
  UpdateUserRequest,
  UserListResponse,
  UserResponse,
} from '@forsys/shared';
import { apiFetch, query } from './client';

// --- users ------------------------------------------------------------------

export interface ListUsersParams extends ListQueryParams {
  /** A search term. Absent or empty lists everybody instead of matching nothing. */
  q?: string;
  includeInactive?: boolean;
}

export function listUsers(params: ListUsersParams = {}): Promise<UserListResponse> {
  return apiFetch<UserListResponse>(`/users${query({ ...params })}`);
}

export function getUser(id: number): Promise<UserResponse> {
  return apiFetch<UserResponse>(`/users/${id}`);
}

export function createUser(body: CreateUserRequest): Promise<UserResponse> {
  return apiFetch<UserResponse>('/users', { method: 'POST', json: body });
}

export function updateUser(id: number, body: UpdateUserRequest): Promise<UserResponse> {
  return apiFetch<UserResponse>(`/users/${id}`, { method: 'PATCH', json: body });
}

export function setUserPassword(id: number, body: SetPasswordRequest): Promise<UserResponse> {
  return apiFetch<UserResponse>(`/users/${id}/password`, { method: 'POST', json: body });
}

/** Require a new password at the user's next request, or stop requiring one. */
export function setPasswordChangeRequired(
  id: number,
  body: SetPasswordChangeRequiredRequest,
): Promise<UserResponse> {
  return apiFetch<UserResponse>(`/users/${id}/password-change-required`, { method: 'PUT', json: body });
}

/**
 * Sign in as this user. The server replaces the session cookie — the app holds
 * no token before or after — so the caller must reload to drop everything it
 * cached as the previous person.
 */
export function impersonateUser(id: number): Promise<UserResponse> {
  return apiFetch<UserResponse>(`/users/${id}/impersonate`, { method: 'POST' });
}

export function deactivateUser(id: number): Promise<UserResponse> {
  return apiFetch<UserResponse>(`/users/${id}/deactivate`, { method: 'POST' });
}

export function activateUser(id: number): Promise<UserResponse> {
  return apiFetch<UserResponse>(`/users/${id}/activate`, { method: 'POST' });
}

/** Groups this user is in, and the roles given to them **directly**. */
export function getUserMemberships(id: number): Promise<MembershipsResponse> {
  return apiFetch<MembershipsResponse>(`/users/${id}/memberships`);
}

// --- groups -----------------------------------------------------------------

export function listGroups(params: ListQueryParams = {}): Promise<GroupListResponse> {
  return apiFetch<GroupListResponse>(`/groups${query({ ...params })}`);
}

export function getGroup(id: number): Promise<GroupResponse> {
  return apiFetch<GroupResponse>(`/groups/${id}`);
}

export function createGroup(body: CreateGroupRequest): Promise<GroupResponse> {
  return apiFetch<GroupResponse>('/groups', { method: 'POST', json: body });
}

/** A group's name and description. Send only what changes; `null` clears the description. */
export function updateGroup(id: number, body: UpdateGroupRequest): Promise<GroupResponse> {
  return apiFetch<GroupResponse>(`/groups/${id}`, { method: 'PATCH', json: body });
}

/** What a delete would take with it, and whether a notification rule blocks it. */
export function getGroupUsage(id: number): Promise<GroupUsageResponse> {
  return apiFetch<GroupUsageResponse>(`/groups/${id}/usage`);
}

/** Cascades to memberships, role assignments and grants. 409 while a rule names it. */
export function deleteGroup(id: number, lockout: Lockout = {}): Promise<DeleteGroupResponse> {
  return apiFetch<DeleteGroupResponse>(`/groups/${id}${lockoutQuery(lockout)}`, {
    method: 'DELETE',
  });
}

export function listGroupMembers(
  id: number,
  params: ListQueryParams = {},
): Promise<GroupMemberListResponse> {
  return apiFetch<GroupMemberListResponse>(`/groups/${id}/members${query({ ...params })}`);
}

/** Idempotent: adding an existing member succeeds and changes nothing. */
export function addGroupMember(id: number, body: AddGroupMemberRequest): Promise<OkResponse> {
  return apiFetch<OkResponse>(`/groups/${id}/members`, { method: 'POST', json: body });
}

export function removeGroupMember(
  id: number,
  userId: number,
  lockout: Lockout = {},
): Promise<OkResponse> {
  return apiFetch<OkResponse>(`/groups/${id}/members/${userId}${lockoutQuery(lockout)}`, {
    method: 'DELETE',
  });
}

// --- roles ------------------------------------------------------------------

export function listRoles(params: ListQueryParams = {}): Promise<RoleListResponse> {
  return apiFetch<RoleListResponse>(`/roles${query({ ...params })}`);
}

export function getRole(id: number): Promise<RoleResponse> {
  return apiFetch<RoleResponse>(`/roles/${id}`);
}

export function createRole(body: CreateRoleRequest): Promise<RoleResponse> {
  return apiFetch<RoleResponse>('/roles', { method: 'POST', json: body });
}

/**
 * What a role is called. Send only what changes.
 *
 * Its permissions are a different endpoint on purpose — see below. A `null`
 * description clears it; leaving the field out keeps the current one.
 */
export function updateRole(id: number, body: UpdateRoleRequest): Promise<RoleResponse> {
  return apiFetch<RoleResponse>(`/roles/${id}`, { method: 'PATCH', json: body });
}

/** Who holds the role, counted — what a delete would take with it. */
export function getRoleUsage(id: number): Promise<RoleUsageResponse> {
  return apiFetch<RoleUsageResponse>(`/roles/${id}/usage`);
}

/** Cascades to every assignment. A built-in role answers 403 `builtin_role`. */
export function deleteRole(id: number, lockout: Lockout = {}): Promise<DeleteRoleResponse> {
  return apiFetch<DeleteRoleResponse>(`/roles/${id}${lockoutQuery(lockout)}`, { method: 'DELETE' });
}

/** The complete set, not a delta — send every permission the role should end with. */
export function setRolePermissions(
  id: number,
  body: UpdateRolePermissionsRequest,
  lockout: Lockout = {},
): Promise<RoleResponse> {
  return apiFetch<RoleResponse>(`/roles/${id}/permissions${lockoutQuery(lockout)}`, {
    method: 'PUT',
    json: body,
  });
}

export function listRoleAssignees(id: number): Promise<RoleAssigneeListResponse> {
  return apiFetch<RoleAssigneeListResponse>(`/roles/${id}/assignees`);
}

export function assignRole(id: number, body: AssignRoleRequest): Promise<OkResponse> {
  return apiFetch<OkResponse>(`/roles/${id}/assignees`, { method: 'POST', json: body });
}

export function unassignRole(
  id: number,
  subjectType: SubjectType,
  subjectId: number,
  lockout: Lockout = {},
): Promise<OkResponse> {
  return apiFetch<OkResponse>(
    `/roles/${id}/assignees/${subjectType}/${subjectId}${lockoutQuery(lockout)}`,
    { method: 'DELETE' },
  );
}

// --- self-lockout -----------------------------------------------------------

/** The confirmation the five calls above take. See the file header. */
export interface Lockout {
  acceptLockout?: boolean;
}

function lockoutQuery(lockout: Lockout): string {
  const parameters: SelfLockoutQuery = lockout.acceptLockout ? { acceptLockout: 'true' } : {};
  return query({ ...parameters });
}

// --- the catalogue and the picker -------------------------------------------

/** Every permission this build defines, flat and grouped. Fixed per build. */
export function listPermissions(): Promise<PermissionListResponse> {
  return apiFetch<PermissionListResponse>('/permissions');
}

/**
 * Users and groups in one list, for the share dialog and the role editor.
 *
 * `email` comes back only for a caller who also holds `user.read`, so anything
 * rendering a subject must cope with it being absent.
 */
export function searchSubjects(
  q: string,
  options: { type?: SubjectType; limit?: number } = {},
): Promise<SubjectSearchResponse> {
  return apiFetch<SubjectSearchResponse>(`/subjects${query({ q, ...options })}`);
}
